import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { setAuth, isStaff } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtErr(detail) {
  if (detail == null) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setForgotSent(true);
      } else if (mode === "register") {
        const { data } = await axios.post(`${API}/auth/register`, form);
        setAuth(data.token, data.user);
        toast.success("Conta criada. Bem-vindo!");
        navigate("/conta");
      } else {
        const { data } = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password });
        setAuth(data.token, data.user);
        if (isStaff()) {
          navigate("/admin/painel");
        } else {
          navigate("/conta");
        }
      }
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-5 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" data-testid="auth-logo" className="inline-flex items-center justify-center select-none">
            <img src="/assets/logo-dark.png" alt="Solution Car Premium" className="h-16 w-auto" />
          </Link>
        </div>

        <div className="bg-white border border-slate-200 shadow-sm p-8 sm:p-10">
          {/* Tabs */}
          {mode !== "forgot" && (
            <div className="grid grid-cols-2 mb-8 border border-slate-200">
              <button onClick={() => setMode("login")} data-testid="tab-login"
                className={`py-3 text-sm font-semibold transition-colors ${mode === "login" ? "bg-[#0072C6] text-white" : "text-slate-500 hover:text-[#0072C6]"}`}>
                Entrar
              </button>
              <button onClick={() => setMode("register")} data-testid="tab-register"
                className={`py-3 text-sm font-semibold transition-colors ${mode === "register" ? "bg-[#0072C6] text-white" : "text-slate-500 hover:text-[#0072C6]"}`}>
                Criar Conta
              </button>
            </div>
          )}

          {mode === "forgot" ? (
            forgotSent ? (
              <div className="text-center py-4" data-testid="forgot-sent">
                <p className="font-display text-xl text-[#0072C6] mb-3">Verifique o seu email</p>
                <p className="text-sm text-slate-600">Se existir uma conta com esse email, enviámos uma ligação para recuperar a palavra-passe (válida por 1 hora).</p>
                <button onClick={() => { setMode("login"); setForgotSent(false); }} className="mt-6 text-sm font-semibold text-[#0072C6] underline">
                  Voltar ao login
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="forgot-form">
                <h1 className="font-display text-2xl text-[#0072C6] mb-2">Recuperar palavra-passe</h1>
                <p className="text-sm text-slate-500 mb-7">Indique o email da sua conta. Enviaremos uma ligação de recuperação.</p>
                <label htmlFor="fp-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Email</label>
                <input id="fp-email" data-testid="forgot-email" type="email" required value={form.email} onChange={set("email")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm mb-6 focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6]" />
                {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">{error}</div>}
                <button type="submit" data-testid="forgot-submit" disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0072C6] text-white font-semibold py-4 text-sm hover:bg-[#005FA3] disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Enviar ligação
                </button>
                <button type="button" onClick={() => setMode("login")} className="mt-5 w-full text-center text-sm text-slate-500 hover:text-[#0072C6]" data-testid="forgot-back">
                  ← Voltar ao login
                </button>
              </form>
            )
          ) : (
            <form onSubmit={submit} data-testid="auth-form">
              <h1 className="font-display text-2xl text-[#0072C6] mb-7">
                {mode === "login" ? "Área de Cliente" : "Criar a sua conta"}
              </h1>
              {mode === "register" && (
                <>
                  <label htmlFor="a-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome completo</label>
                  <input id="a-name" data-testid="register-name" type="text" required minLength={2} value={form.name} onChange={set("name")}
                    className="w-full border border-slate-200 px-4 py-3 text-sm mb-5 focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6]" />
                </>
              )}
              <label htmlFor="a-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Email</label>
              <input id="a-email" data-testid="auth-email" type="email" required value={form.email} onChange={set("email")}
                className="w-full border border-slate-200 px-4 py-3 text-sm mb-5 focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6]" />
              {mode === "register" && (
                <>
                  <label htmlFor="a-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Telefone</label>
                  <input id="a-phone" data-testid="register-phone" type="tel" value={form.phone} onChange={set("phone")}
                    className="w-full border border-slate-200 px-4 py-3 text-sm mb-5 focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6]" />
                </>
              )}
              <label htmlFor="a-password" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                Palavra-passe {mode === "register" && <span className="normal-case font-normal text-slate-400">(mín. 8 caracteres)</span>}
              </label>
              <input id="a-password" data-testid="auth-password" type="password" required minLength={mode === "register" ? 8 : 1}
                value={form.password} onChange={set("password")}
                className="w-full border border-slate-200 px-4 py-3 text-sm mb-6 focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6]" />

              {error && <div className="mb-5 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="auth-error">{error}</div>}

              <button type="submit" data-testid="auth-submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0072C6] text-white font-semibold py-4 text-sm hover:bg-[#005FA3] disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "login" ? "Entrar" : "Criar Conta"}
              </button>

              {mode === "login" && (
                <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-link"
                  className="mt-5 w-full text-center text-sm text-slate-500 hover:text-[#0072C6]">
                  Esqueci-me da palavra-passe
                </button>
              )}
            </form>
          )}
        </div>
        <p className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-[#0072C6]" data-testid="auth-back-home">← Voltar ao website</Link>
        </p>
      </div>
    </div>
  );
}
